'use client';

import { Grid, GridItem, Link, Loader, Modal, ModalBody } from '@coolblue-development/freddy';
import selectMainProductTrigger from '@coolblue-development/ga4-events/triggers/StoreStockOverlay/selectMainProductTrigger';
import selectStoreLocationTrigger from '@coolblue-development/ga4-events/triggers/StoreStockOverlay/selectStoreLocationTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useClientLogger } from '@coolblue-development/next-config/client';
import { useFreddyTranslations } from '@coolblue-development/next-design-system-utils/client';
import {
  ProductTitle,
  SelectableBoxGroup,
  SquareProductImage
} from '@coolblue-development/next-patterns/client';
import { SalesPrice, SalesPriceCurrent } from '@coolblue-development/next-sales-price/client';
import styled from '@emotion/styled';
import getProductStoreAvailabilities from '@features/product/components/StoreStockModal/actions/getProductStoreAvailabilities';
import PreOrderNotification from '@features/product/components/StoreStockModal/PreOrderNotification';
import StoreAvailabilityCard from '@features/product/components/StoreStockModal/StoreAvailabilityCard';
import StorePickupOnlyNotification from '@features/product/components/StoreStockModal/StorePickupOnlyNotification';
import { AvailabilityState, Product } from '@features/product/typings';
import { StoreAvailability } from '@features/product-page/api/queries/productStoreAvailabilitiesQuery';
import { useBreakpoints } from '@hooks/useBreakpoints';
import { useTranslation } from '@i18n/useTranslation';
import { useCallback, useEffect, useState } from 'react';

type Props = {
  open: boolean;
  onClose: () => void;
  product: Product;
  dateOverride?: string;
};

function StoreStockModal({ open, onClose, product, dateOverride }: Props) {
  const logger = useClientLogger();
  const { trackEvent } = useAnalytics();
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string>('');
  const [storeAvailabilities, setStoreAvailabilities] = useState<StoreAvailability[] | null>(null);
  const { t } = useTranslation('webshop.productPage');
  const modalTranslations = useFreddyTranslations('modal');
  const { isMobile } = useBreakpoints();
  const isPreOrder = product.availabilityState === AvailabilityState.PreOrder;
  const isNotAvailableOnline =
    product.availabilityState === AvailabilityState.OutOfStock ||
    product.availabilityState === AvailabilityState.TemporaryOutOfStock;

  const trackSelectStore = useCallback(
    (stockLocationId?: string, storeName?: string) => {
      if (!stockLocationId || !storeName) return;

      trackEvent(
        selectStoreLocationTrigger({
          store_id: Number(stockLocationId),
          store_name: storeName
        })
      );
    },
    [trackEvent]
  );

  useEffect(() => {
    if (!storeAvailabilities && open) {
      getProductStoreAvailabilities(product.id)
        .then(data => {
          setStoreAvailabilities(data);
          if (!selected) {
            trackSelectStore(data?.[0]?.stockLocationId, data?.[0]?.store?.name);
            setSelected(String(data?.[0]?.stockLocationId));
          }
          setLoading(false);
        })
        .catch(error => {
          logger.error(`Failed to get store stock availabilities`, error);

          setLoading(false);
        });
    }
  }, [open, product.id, selected, storeAvailabilities, trackSelectStore, logger]);

  const handleMainProductClick = () => {
    trackEvent(
      selectMainProductTrigger({
        index: 1,
        total_indices: 1,
        item_id: product.id,
        sub_feature: 'generic_store_stock_overlay'
      })
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      placement={isMobile ? 'bottom' : 'center'}
      size='large'
      sx={{ minHeight: 200 }}
      title={t('storePickup.modalTitle')}
      translations={modalTranslations}
      body={
        <StyledModalBody>
          {loading && <Loader />}
          {storeAvailabilities && (
            <>
              <Grid gap={200} mb={400} templateColumns='auto 1fr'>
                <GridItem>
                  <SquareProductImage
                    size='small'
                    imageId={String(product.primaryImage?.id)}
                    alt='' // As the product name is displayed, the alt attribute is redundant here.
                    loading='eager'
                  />
                </GridItem>
                <GridItem>
                  <ProductTitle>
                    <ProductTitle.Title>
                      <Link href='#' onClick={handleMainProductClick}>
                        {product.name}
                      </Link>
                    </ProductTitle.Title>
                  </ProductTitle>
                  <SalesPrice
                    currentPrice={<SalesPriceCurrent price={product.salesPrice.includingVat} />}
                  />
                </GridItem>
              </Grid>

              {isPreOrder && product.expectedReleaseDate && (
                <PreOrderNotification expectedReleaseDate={product.expectedReleaseDate} />
              )}

              {isNotAvailableOnline && <StorePickupOnlyNotification />}

              <SelectableBoxGroup
                onChange={value => {
                  setSelected(value);
                }}
                value={selected}
                name='location'
                type='radio'
                inputPlacement='hidden'
              >
                {storeAvailabilities?.map(item => (
                  <StoreAvailabilityCard
                    key={item.stockLocationId}
                    isActive={selected === String(item.stockLocationId)}
                    setIsActive={() => setSelected(String(item.stockLocationId))}
                    productId={product.id}
                    onlineAvailabilityState={product.availabilityState}
                    releaseDate={product.expectedReleaseDate}
                    storeAvailability={item}
                    trackSelectStore={trackSelectStore}
                    dateOverride={dateOverride}
                  />
                ))}
              </SelectableBoxGroup>
            </>
          )}
        </StyledModalBody>
      }
    />
  );
}

const StyledModalBody = styled(ModalBody)`
  position: relative;
`;

export default StoreStockModal;
